<script setup lang="ts">
import { useDebounceFn, useVModel } from '@vueuse/core'
import axios from 'axios'

import { getValidityInDaysDisclaimer } from '@/components/rewards/utils/rewardHelpers'
import RewardFormBlock from '@/views/reward/components/RewardFormBlock.vue'
import LegacyRewardModal from '@/views/reward/components/RewardModal.vue'
import NewRewardModal from '@/components/rewards/RewardModal.vue'
import { buildContestRewardSavePayload, getCustomVars } from '../utils'

import type { ContestGameReward, LoyaltyReward } from '@/types/types'
import { useRewardForm } from '@/components/rewards/compmosable/useRewardForm'

const props = defineProps<{
  contestReward: ContestGameReward | null
  selectedReward: LoyaltyReward | null
  channelType: string | null
  dissociateOnCancel?: boolean
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'selected-reward:saved', event: { rewardId: number; contestReward: ContestGameReward }): void
  (e: 'remove-from-contest'): void
}>()

const selectedReward = useVModel(props, 'selectedReward', emit)
const contestReward = useVModel(props, 'contestReward', emit)

const { t, d } = useI18n()

const { saveReward } = useRewardForm()
const { user } = useLocalStorage()
const { getStoresByPermission } = useStore()
const notification = useNotification()

const { flags } = useFeatureFlag()

const RewardModal = computed(() => {
  return flags.value['offer-engine']?.enabled ? NewRewardModal : LegacyRewardModal
})

const isContestSettingTouched = ref(true)
const hasMessageContentError = ref(false)

const rewardNotificationStoreIds = computed(() =>
  getStoresByPermission('reward.read').map((store: { id: number }) => store.id)
)

const probability = ref()

const nameInputValue = ref(contestReward.value.name)

const messageLength = ref(0)
const isCheckingMessageLength = ref(false)
const variablesLength = ref<Record<string, number>>({})

const messageMaxLength = computed(() => (props.channelType === 'sms' ? 160 : 10000))

const hasCompatibleVoucherIntegration = computed(() =>
  Boolean((user.value?.merchant as any)?.hasCompatibleVoucherIntegration)
)

const customVariables = computed(() =>
  getCustomVars(
    { ...contestReward.value, name: nameInputValue.value },
    variablesLength.value,
    t,
    hasCompatibleVoucherIntegration.value
  )
)

const handleSaveReward = async (reward: LoyaltyReward) => {
  try {
    if (!reward) {
      notification.create('error', {
        text: t('pages.reward.errors.rewardNotFound')
      })
      return
    }

    const payload = buildContestRewardSavePayload(reward, !!flags.value['offer-engine']?.enabled)
    if (!payload) return

    const savedReward = await saveReward(payload)

    if (reward.id) {
      notification.create('success', {
        text: t('pages.reward.editReward.success')
      })
    } else {
      notification.create('success', {
        text: t('pages.reward.createReward.success')
      })
    }

    emit('selected-reward:saved', { rewardId: savedReward.id, contestReward: contestReward.value })
  } catch {
    notification.create('error', {
      text: t('pages.reward.editReward.error')
    })
  }
}

const closeCreateEditRewardModal = async () => {
  if (props.dissociateOnCancel) {
    emit('remove-from-contest')
  }
  emit('close')
}

const handleRewardTypeChanged = (reward: LoyaltyReward) => {
  selectedReward.value = {
    ...selectedReward.value,
    rewardType: reward.rewardType
  }
}

const handleProbabilityChange = (value: number) => {
  contestReward.value.probability = value
  isContestSettingTouched.value = true
}

const validityInDaysDisclaimer = computed(() => {
  return getValidityInDaysDisclaimer(contestReward.value.validityInDays, d, t)
})

const handlePointsChange = () => {
  isContestSettingTouched.value = true
  handleMessageLengthCalculation()
}

const handleNameChange = (name: string) => {
  nameInputValue.value = name
  handleMessageLengthCalculation()
}

const handleMessageLengthCalculation = () => {
  isCheckingMessageLength.value = true
  messageLength.value = null

  getMessageLength()
}

const getMessageLength = useDebounceFn(async () => {
  if (!contestReward.value.notificationTextOverride?.length) {
    messageLength.value = 0
    isCheckingMessageLength.value = false
  } else {
    try {
      const { data } = await axios.get('contest-games/message-length', {
        params: {
          message: contestReward.value.notificationTextOverride,
          rewardName: nameInputValue.value,
          points: contestReward.value.points || 0,
          type: props.channelType
        }
      })
      messageLength.value = data.data.messageLength

      isCheckingMessageLength.value = false
    } catch {
      messageLength.value = 0
      isCheckingMessageLength.value = false
      notification.create('error', {
        text: t('pages.campaign.campaignCreatePage.notifications.test.messageLengthCalculationError')
      })
    }
  }
}, 500)

const getVariablesLength = async () => {
  try {
    const { data } = await axios.get('contest-games/variables-length', {
      params: {
        rewardName: nameInputValue.value,
        points: contestReward.value.points || 0,
        type: props.channelType
      }
    })
    variablesLength.value = data.data
  } catch {
    notification.create('error', {
      text: t('pages.campaign.campaignCreatePage.notifications.test.messageLengthCalculationError')
    })
  }
}

const handleMessageContentError = (error: boolean) => {
  hasMessageContentError.value = error
}

onMounted(async () => {
  probability.value = contestReward.value.probability

  await getVariablesLength()

  if (contestReward.value.notificationTextOverride) {
    handleMessageLengthCalculation()
  }
})
</script>

<template>
  <RewardModal
    :reward-id="selectedReward?.id"
    :is-external-form-modified="isContestSettingTouched"
    usage="contest"
    :has-errors="hasMessageContentError"
    hide-claim-limit-block
    @close="closeCreateEditRewardModal"
    @update:reward="handleSaveReward"
    @update:rewardTypeChanged="handleRewardTypeChanged"
    @update:nameChanged="handleNameChange"
  >
    <template #additional-form-blocks>
      <RewardFormBlock :title="t('pages.reward.detail.contestOptions')">
        <div>
          <label class="mb-2 flex items-center gap-1 text-sm text-foreground">
            <span>{{ t('pages.contests.contestsCreatePage.rewardFields.probability') }}</span>
          </label>
          <AppFormInput
            id="probability"
            v-model="probability"
            type="number"
            :min="0"
            :max="100"
            step="1"
            @update:model-value="handleProbabilityChange"
          >
            <template #icon-right>
              <div class="text-sm text-muted-foreground h-5 flex items-center justify-center">%</div>
            </template>
          </AppFormInput>
        </div>

        <div>
          <label class="mb-2 flex items-center gap-1 text-sm text-foreground">
            <span>{{ t('pages.contests.contestsCreatePage.rewardFields.points') }}</span>
          </label>
          <AppFormInput
            id="points"
            v-model="contestReward.points"
            type="number"
            :min="0"
            step="1"
            placeholder="0"
            @update:model-value="handlePointsChange"
          >
            <template #icon-right>
              <div class="text-sm text-muted-foreground h-5 flex items-center justify-center">
                {{ t('global.words.points') }}
              </div>
            </template>
          </AppFormInput>
        </div>

        <div>
          <label class="mb-2 flex items-center gap-1 text-sm text-foreground">
            <span>{{ t('pages.contests.contestsCreatePage.rewardFields.duration') }}</span>
          </label>
          <AppFormInput
            id="duration"
            v-model="contestReward.validityInDays"
            type="number"
            placeholder="∞"
            :min="0"
            :max="365"
            step="1"
            @update:model-value="isContestSettingTouched = true"
          >
            <template #icon-right>
              <div class="text-sm text-muted-foreground h-5 flex items-center justify-center">
                {{ t('global.words.days') }}
              </div>
            </template>
          </AppFormInput>

          <p class="text-xs text-muted-foreground mt-1">{{ validityInDaysDisclaimer }}</p>
        </div>
      </RewardFormBlock>

      <RewardFormBlock :title="t('pages.reward.detail.notificationsOptions')">
        <TextareaWithVars
          v-model="contestReward.notificationTextOverride"
          label="about"
          :max-length="messageMaxLength"
          :i18n-strings="{
            textareaLabel: t(
              `pages.contests.contestsCreatePage.message.form.fields.notificationTextOverride.${channelType}`
            ),
            characters: t('global.words.characters'),
            insertVar: t('global.customVars.insertLabel'),
            errorEmojiInMessage: t('ui.error.strings.unauthorizedEmoji'),
            customValue: {
              placeholder: t('global.customVars.customValue.placeholder'),
              create: t('global.customVars.customValue.add'),
              update: t('global.customVars.customValue.modify'),
              back: t('global.customVars.customValue.back')
            }
          }"
          :custom-vars="customVariables"
          :sanitize-for-gsm="channelType === 'sms'"
          :required-store-ids="rewardNotificationStoreIds"
          @update:model-value="handleMessageLengthCalculation"
          @has-error="handleMessageContentError"
        >
          <template #messageLength>
            <span v-if="isCheckingMessageLength"> {{ t('global.words.loading') }}... </span>
            <span v-else>
              <span :class="{ 'text-red-600': messageLength > messageMaxLength }">
                {{ messageLength }}/{{ messageMaxLength }} {{ t('global.words.characters') }}
              </span>
            </span>
          </template>
        </TextareaWithVars>
      </RewardFormBlock>
    </template>

    <template #additional-summary> </template>
  </RewardModal>
</template>
